import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "./components/DashboardLayout";
import { useAuth } from "@/hooks/useAuth";
import { getMyListings, getFavoritesCount, getHistoryCount, MyListingItem } from "@/utils/api";

function StatCard({ stat, index }: { stat: { label: string; value: number; path: string; icon: string; color: string }; index: number }) {
  const [ref, isVisible] = useState({ current: null });
  
  useEffect(() => {
    const el = document.getElementById(`stat-${index}`);
    if (el) {
      setTimeout(() => {
        el.classList.add("opacity-100", "translate-y-0");
      }, index * 100);
    }
  }, [index]);

  return (
    <div
      id={`stat-${index}`}
      className="transition-all duration-500 opacity-0 translate-y-6"
    >
      <Link
        to={stat.path}
        className="flex items-center gap-4 p-4 rounded-xl border border-gray-100 hover:border-orange-200 transition-all duration-300 hover:shadow-sm"
      >
        <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${stat.color}`}>
          <i className={`${stat.icon} text-xl`} />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
          <p className="text-sm text-gray-500">{stat.label}</p>
        </div>
      </Link>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    listings: 0,
    favorites: 0,
    history: 0,
  });
  const [recentListings, setRecentListings] = useState<MyListingItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // 并行加载所有数据
      const [listingsRes, favoritesCount, historyCount] = await Promise.all([
        getMyListings({ page: 1, page_size: 3 }).catch(() => ({ total: 0, list: [] })),
        getFavoritesCount().catch(() => 0),
        getHistoryCount().catch(() => 0),
      ]);
      
      setStats({
        listings: listingsRes.total || 0,
        favorites: favoritesCount,
        history: historyCount,
      });
      setRecentListings(listingsRes.list || []);
    } catch (e) {
      console.error('加载数据失败:', e);
    } finally {
      setLoading(false);
    }
  };

  const statsData = [
    { label: "我的发布", value: stats.listings, path: "/dashboard/my-listings", icon: "ri-file-list-line", color: "bg-blue-50 text-blue-600" },
    { label: "我的收藏", value: stats.favorites, path: "/dashboard/favorites", icon: "ri-heart-line", color: "bg-red-50 text-red-600" },
    { label: "浏览记录", value: stats.history, path: "/dashboard/history", icon: "ri-history-line", color: "bg-green-50 text-green-600" },
  ];

  const getStatusLabel = (status: string) => {
    const map: Record<string, string> = {
      active: '已发布',
      pending: '待审核',
      offline: '已下架',
      rejected: '已驳回',
    };
    return map[status] || status;
  };

  const getStatusClass = (status: string) => {
    const map: Record<string, string> = {
      active: 'bg-green-50 text-green-600',
      pending: 'bg-yellow-50 text-yellow-600',
      offline: 'bg-gray-50 text-gray-500',
      rejected: 'bg-red-50 text-red-600',
    };
    return map[status] || 'bg-gray-50 text-gray-500';
  };

  return (
    <DashboardLayout>
      <div className="p-5 md:p-6">
        <h1 className="text-lg md:text-xl font-bold text-gray-800 mb-6">
          会员中心
        </h1>

        {/* Welcome */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-400 rounded-xl p-5 md:p-6 text-white mb-6">
          <div className="flex items-center gap-4">
            <img
              src={user?.avatar}
              alt={user?.nickname}
              className="w-14 h-14 rounded-full object-cover border-2 border-white/30"
            />
            <div>
              <h2 className="text-lg font-semibold">
                欢迎回来，{user?.nickname}
              </h2>
              <p className="text-sm text-white/80 mt-0.5">
                管理您的服务信息，发现更多商业机会
              </p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {statsData.map((stat, index) => (
            <StatCard key={stat.path} stat={stat} index={index} />
          ))}
        </div>

        {/* Quick Actions */}
        <div className="mb-6">
          <h3 className="text-base font-semibold text-gray-800 mb-3">
            快捷操作
          </h3>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/publish"
              className="flex items-center gap-2 px-4 py-2.5 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-all duration-300 text-sm hover:shadow-md hover:scale-105"
            >
              <i className="ri-add-line" />
              发布新信息
            </Link>
            <Link
              to="/dashboard/profile"
              className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition-all duration-300 text-sm hover:scale-105"
            >
              <i className="ri-user-settings-line" />
              编辑资料
            </Link>
          </div>
        </div>

        {/* Recent Listings */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-gray-800">
              最近发布
            </h3>
            <Link
              to="/dashboard/my-listings"
              className="text-sm text-orange-600 hover:text-orange-700"
            >
              查看全部
            </Link>
          </div>
          
          {loading ? (
            <div className="text-center py-8">
              <i className="ri-loader-4-line text-2xl text-gray-400 animate-spin" />
              <p className="text-gray-500 mt-2">加载中...</p>
            </div>
          ) : recentListings.length > 0 ? (
            <div className="space-y-3">
              {recentListings.map((listing) => (
                <Link
                  key={listing.id}
                  to={`/listing/${listing.id}`}
                  className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-orange-200 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 shrink-0">
                    {listing.images && listing.images[0] ? (
                      <img
                        src={listing.images[0]}
                        alt={listing.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <i className="ri-image-line" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {listing.title}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {listing.view_count} 次浏览 · {listing.category_name}
                    </p>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full shrink-0 ${getStatusClass(listing.status)}`}>
                    {getStatusLabel(listing.status)}
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-gray-100">
                <i className="ri-file-list-line text-2xl text-gray-400" />
              </div>
              <p className="text-gray-500 mb-3">暂无发布信息</p>
              <Link
                to="/publish"
                className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm"
              >
                <i className="ri-add-line" />
                发布第一条信息
              </Link>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
